import * as Sentry from "@sentry/react";

/**
 * Sentry Configuration
 * 
 * Features enabled:
 * - Error tracking and reporting
 * - Performance tracing
 * - Session replay
 * - User feedback
 * 
 * Testing:
 * - In development mode, use the SentryTest component to test error reporting and feedback
 * - "Trigger Test Error" button will simulate an error and report it to Sentry
 * - "Open Feedback Widget" button will open the feedback form
 * 
 * For production deployment:
 * - Remove the SentryTest component
 * - Adjust the sample rates based on your traffic volume
 */

// Initialize Sentry with feedback integration
const initSentry = () => {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
      // Add feedback integration
      // https://docs.sentry.io/platforms/javascript/user-feedback/configuration/
      Sentry.feedbackIntegration({
        autoInject: false, // Disable auto-inject to control where the button appears
        colorScheme: "system",
        enableScreenshot: true,
        showBranding: false,
        
        // Portuguese (Brazilian) localization
        triggerLabel: "Feedback",
        formTitle: "Enviar Feedback",
        successMessageText: "Obrigado pelo seu feedback!",
        nameLabel: "Nome",
        namePlaceholder: "Seu nome",
        emailLabel: "E-mail",
        emailPlaceholder: "Seu e-mail",
        messageLabel: "Mensagem",
        messagePlaceholder: "Conte-nos sobre qualquer problema ou sugestão para melhorar sua experiência",
        submitButtonLabel: "Enviar",
        cancelButtonLabel: "Cancelar",
        addScreenshotButtonLabel: "Incluir captura de tela",
        
        // Form requirements
        nameRequired: false,
        emailRequired: false,
        messageRequired: true,
      }),
    ],
    
    // Tracing
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    profilesSampleRate: import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE,

    // Session Replay
    replaysSessionSampleRate: import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 
    replaysOnErrorSampleRate: import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    
    // Environment
    environment: import.meta.env.MODE,
    
    // Error filtering - ignore non-critical errors
    beforeSend(event, hint) {
      const error = hint.originalException;
      
      // Filter out DOM manipulation errors that don't affect user experience
      if (error instanceof Error) {
        // Ignore benign DOM errors
        if (
          error.message.includes('removeChild') ||
          error.message.includes('Node') ||
          error.message.includes('ResizeObserver loop') ||
          error.message.includes('Non-Error promise rejection')
        ) {
          // Log to console in development but don't send to Sentry
          if (import.meta.env.DEV) {
            console.warn('Filtered Sentry error:', error.message);
          }
          return null; // Don't send to Sentry
        }
      }
      
      return event;
    },
    
    // Ignore specific error types
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'fb_xd_fragment',
      // Random network errors
      'NetworkError',
      'Network request failed',
      // Aborted requests
      'AbortError',
      'Request aborted',
      // Resize observer errors (common and harmless)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });
};

// Function to get feedback instance
const getFeedback = () => {
  return Sentry.getFeedback();
};

export { initSentry, Sentry, getFeedback }; 